// PANEL-KÁRTYA VÁZ — a kezdőlap- és összefoglaló-kártyák EGY közös alakja MINDKÉT felületen.
//
// MIÉRT (user-döntés): a kártyák külön-külön hozták a fejlécüket, a betöltés-csontvázukat és az
// üres-állapotukat, ezért a fejléc-magasságok és a térközök apró eltérésekkel „billegtek" egymás mellett. Ez a
// váz KÉNYSZERÍTI az egységet: azonos fejléc (cím + opcionális „Mind megnyitása"), azonos csontváz, azonos
// üres-állapot-padding, és a fejléc alatt BEHÚZOTT hajszálvonal — ugyanaz a vonal, mint a lista-táblánkban.
//
// MIÉRT A KÖZÖS RÉTEGBEN: a PARTNER-PORTÁL kezdőlapja ugyanezt kérte, és nyers `Card` +
// `CardHeader` + `divide-y` alakban készült — teljes szélességű fejléc-vonallal és a kártya széléig futó
// sor-vonalakkal, azaz pontosan azzal a két hibával, amit ez a váz kizár (a user mérte a felületen). A repo
// 2. használat-szabálya szerint tehát ide került `features/dashboard`-ból, `PanelCard` → `PanelCard`
// néven (a név már nem egy felület kezdőlapját jelenti).
//
// A `Card` alap `CardHeader`-e teljes szélességű `border-b`-t ad; itt azt KIKAPCSOLJUK (`border-b-0`), és a
// vonalat a közös `Divider`-rel húzzuk be (`inset`, `hairline`) — így a kártya-fejléc és a lista-fejléc vonala
// azonos alakú.
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { CardOpenAll } from '@/components/shared/card-open-all';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export interface PanelCardProps {
  title: string;
  /** Ha van: „Mind megnyitása" link a fejlécben, erre az útvonalra (opcionális szűrővel). */
  openAllTo?: string;
  openAllSearch?: Record<string, string>;
  /**
   * Opcionális JELÖLŐ a cím mellett (pl. darabszám-badge). SZÁNDÉKOSAN nem szabad slot: a fejléc
   * összeállítása kártyánként épp az, ami a magasságokat elbillentette — ide csak a cím MELLÉ való,
   * alacsony elem kerülhet (badge/chip), akció-gomb nem.
   */
  badge?: ReactNode;
  /**
   * Opcionális AKCIÓ vagy META a fejléc jobb szélén — csak ALACSONY, link/szöveg-jellegű elem (szöveg-gomb,
   * kis link, időpont-meta), mint a „Mind megnyitása". MIÉRT VAN MÉGIS: a jegy-részlet
   * „Jegy-hozzáférés" kártyája fejléc-akciót kér („Szerkesztés"), az „Igény" kártya pedig a nyitás
   * időpontját írja ki — és az `openAllTo` csak NAVIGÁCIÓRA jó. A fejléc MAGASSÁGÁT továbbra is a cím
   * tipográfiája adja (a slotba `Button` nem kerül), tehát a kártya-fejlécek egységessége nem sérül.
   * Az `openAllTo`-val EGYSZERRE nem használjuk (egy kártyának egy fejléc-akciója van).
   */
  action?: ReactNode;
  /** Betöltés → egységes csontváz (nem kártyánként más). */
  loading?: boolean;
  /** Hány csontváz-sort mutassunk betöltés közben (alap: 2 — a legtöbb kártya rövid). */
  skeletonRows?: number;
  /**
   * A kártya KITÖLTI a rendelkezésre álló magasságot (a szülő rács/flex-sáv magasságát), a törzs pedig
   * megnyúlik. MIÉRT OPT-IN: a kezdőlap-rácsban EGY kártya „vezet" (a hosszabb lista), a mellette álló
   * összefoglaló-kártya pedig különben tartalom-magas maradt, és a hasáb alja csorbán állt — a user
   * pontosan ezt mérte a partner-kezdőlapon. Alapból KIKAPCSOLT: a belső kezdőlap kártyái szándékosan
   * tartalom-magasak (ott a rács minden eleme hasonló hosszú).
   */
  fill?: boolean;
  /**
   * A kártya ENGEDI a `position: sticky` gyerekét (levéve a `overflow-hidden`-t).
   *
   * MIÉRT KELL OPT-IN KAPU (és miért nem alapból): a `Card` `overflow-hidden`-je azért van, hogy a tartalom
   * ne fusson ki a lekerekített sarkokon. Csak épp egy `overflow: hidden` ős a `sticky` gyerek
   * SCROLL-KONTÉNERE lesz — a ragadós sáv így a KÁRTYA aljához tapad, vagyis oda, ahol amúgy is állt: a
   * hatás elmarad (mérve a portál jegy-szálán: a komment-sáv 9145px-en, a fold alatt ült). A kaput CSAK az
   * a kártya nyitja meg, amelyiknek ragadós sávja van, és a sáv MAGA hordozza a `rounded-b-xl`-t, hogy a
   * kártya alsó sarkai ne szögletesedjenek el.
   */
  stickyContent?: boolean;
  /**
   * MAGASSÁG-PLAFON + BELSŐ GÖRGETÉS (a kezdőlap-rács csempéi): a kártya kitölti a rács-sor magasságát (`fill`),
   * de legfeljebb `max-h-96` (24rem) magas; a törzs ezen belül görget, és alul halvány kifutás jelzi, hogy van
   * még tartalom. MIÉRT: egy hosszú agenda/lista nem nyújthatja a sort, és a mellette álló rövid csempe sem
   * maradhat hatalmas üres kártya. Implikálja a `fill`-t. A többi felület nem használja.
   */
  capped?: boolean;
  children: ReactNode;
}

/** A plafon: a Tailwind-skála 96-os értéke (24rem) — nincs önkényes px. */
export const PANEL_CARD_MAX_HEIGHT_CLASS = 'max-h-96';

/** Görgethető törzs alsó kifutással: a kifutás CSAK akkor látszik, ha alul van még el nem ért tartalom. */
function CappedBody({ children }: { children: ReactNode }): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);

  const update = useCallback((): void => {
    const el = scrollRef.current;
    if (!el) return;
    setMore(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  }, []);

  useEffect(() => {
    update();
    // a tartalom (lista-hossz) és a keret mérete változhat → újraszámolás; régi/teszt-környezetben nincs observer
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    if (scrollRef.current) ro.observe(scrollRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [update]);

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={update}
        data-testid="panel-card-scroll"
        className="min-h-0 flex-1 overflow-y-auto"
      >
        <div ref={innerRef}>{children}</div>
      </div>
      <div
        aria-hidden
        data-testid="panel-card-fade"
        className={cn(
          'from-card pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t to-transparent transition-opacity',
          more ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}

export function PanelCard({
  title,
  openAllTo,
  openAllSearch,
  badge,
  action,
  loading = false,
  skeletonRows = 2,
  fill = false,
  stickyContent = false,
  capped = false,
  children,
}: PanelCardProps): JSX.Element {
  const filled = fill || capped;
  return (
    <Card
      className={cn(
        filled && 'flex h-full flex-col',
        capped && PANEL_CARD_MAX_HEIGHT_CLASS,
        stickyContent && 'overflow-visible',
      )}
    >
      {/* A FEJLÉC egységes magasságú: a cím tipográfiája adja, a link kisebb — így minden kártya-fejléc egyforma. */}
      <CardHeader className="border-b-0">
        <div className="flex min-w-0 items-center gap-2">
          <CardTitle className="truncate">{title}</CardTitle>
          {badge}
        </div>
        {openAllTo !== undefined && (
          <CardOpenAll to={openAllTo} {...(openAllSearch ? { search: openAllSearch } : {})} />
        )}
        {action}
      </CardHeader>
      <Divider spacing="none" tone="hairline" />
      {/* `fill` esetén a TÖRZS is flex-oszlop: így a benne álló elem (üres-állapot, saját blokk) MEGNYÚLHAT a
          kártya aljáig. Enélkül a maradék hely a tartalom ALATT gyűlt össze, és az üres-állapot szaggatott
          kerete alul nagyobb térközzel állt, mint fölül/oldalt (user-lelet, 2026-09-08). */}
      <CardContent className={filled ? 'flex min-h-0 flex-1 flex-col p-0' : 'p-0'}>
        {loading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: skeletonRows }, (_, i) => (
              <Skeleton key={i} className="h-9 rounded-lg" />
            ))}
          </div>
        ) : capped ? (
          <CappedBody>{children}</CappedBody>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

/** Egységes üres-állapot a panel-kártyákban: azonos padding és tipográfia (nem kártyánként külön). */
export function PanelCardEmpty({ children }: { children: ReactNode }): JSX.Element {
  return <div className="text-muted-foreground space-y-1 p-4 text-sm">{children}</div>;
}
